import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import DecryptedText from './ui/DecryptedText';
import BlurText from './ui/BlurText';
import Magnet from './ui/Magnet';

const Hero: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);

  // Parallax: content drifts up, fades, and shrinks as the hero scrolls away
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  });
  const contentY = useTransform(scrollYProgress, [0, 1], ['0%', '35%']);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);
  const contentScale = useTransform(scrollYProgress, [0, 0.8], [1, 0.92]);
  const indicatorOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0]);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 80, behavior: 'smooth' });
  };

  return (
    <section id="home" ref={sectionRef} className="min-h-screen flex items-center justify-center relative px-6">
      <motion.div
        style={{ y: contentY, opacity: contentOpacity, scale: contentScale }}
        className="text-center max-w-4xl mx-auto"
      >
        {/* Greeting */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="mb-4"
        >
          <span className="text-base md:text-lg text-gray-400 font-light tracking-[0.3em] uppercase">
            <DecryptedText
              text="Hello, I'm"
              speed={40}
              maxIterations={12}
              animateOn="mount"
            />
          </span>
        </motion.div>

        {/* Name — words are unbreakable units so wrapping never splits mid-word */}
        <h1
          className="text-5xl md:text-7xl lg:text-8xl font-bold gradient-text mb-2"
          aria-label="Abdullah Ahmad"
        >
          {"Abdullah Ahmad".split(' ').map((word, wi, words) => {
            const letterOffset = words.slice(0, wi).reduce((n, w) => n + w.length, 0);
            return (
              <React.Fragment key={wi}>
                <span className="inline-block whitespace-nowrap">
                  {word.split('').map((ch, i) => (
                    <motion.span
                      key={i}
                      className="inline-block"
                      initial={{ opacity: 0, y: 80, rotateX: -60 }}
                      animate={{ opacity: 1, y: 0, rotateX: 0 }}
                      transition={{
                        duration: 0.7,
                        ease: [0.22, 1, 0.36, 1],
                        delay: 0.5 + (letterOffset + i) * 0.035,
                      }}
                    >
                      {ch}
                    </motion.span>
                  ))}
                </span>
                {wi < words.length - 1 && ' '}
              </React.Fragment>
            );
          })}
        </h1>

        {/* Scribble underline — draws in once the letters have landed */}
        <svg
          className="mx-auto mb-6 w-[260px] md:w-[380px] lg:w-[460px]"
          viewBox="0 0 460 28"
          fill="none"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="hero-scribble-grad" x1="0" y1="0" x2="460" y2="0" gradientUnits="userSpaceOnUse">
              <stop offset="0" stopColor="#667eea" />
              <stop offset="0.5" stopColor="#a78bfa" />
              <stop offset="1" stopColor="#764ba2" />
            </linearGradient>
          </defs>
          <motion.path
            d="M8 18 C 90 6, 200 22, 290 12 S 430 14, 452 9 M70 23 C 160 16, 300 24, 396 18"
            stroke="url(#hero-scribble-grad)"
            strokeWidth="3.5"
            strokeLinecap="round"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 1.1, delay: 1.4, ease: [0.65, 0, 0.35, 1] }}
          />
        </svg>

        {/* Title */}
        <div className="text-xl md:text-2xl lg:text-3xl text-gray-300 mb-6 font-light">
          <BlurText
            text="Jr. Integration Specialist | Cloud & API Engineer"
            animateBy="words"
            direction="top"
            delay={120}
            stepDuration={0.45}
          />
        </div>

        {/* Description */}
        <div className="text-base md:text-lg text-gray-400 mb-12 max-w-2xl mx-auto leading-relaxed">
          <BlurText
            text="Building and migrating API proxies on Google Cloud. Focused on Apigee X/Hybrid, cloud infrastructure, and integration engineering."
            animateBy="words"
            direction="bottom"
            delay={60}
            stepDuration={0.35}
          />
        </div>

        {/* Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.8, duration: 0.6 }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
        >
          <Magnet padding={60} magnetStrength={4}>
            <motion.button
              onClick={() => scrollTo('experience')}
              className="px-8 py-4 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-primary-500/30 transition-all duration-300 pulse-glow"
              whileTap={{ scale: 0.95 }}
            >
              View My Work
            </motion.button>
          </Magnet>

          <Magnet padding={60} magnetStrength={4}>
            <motion.a
              href="https://drive.google.com/file/d/1jsWVOfA53shl6c8Ph4jM-mLttI_veG7j/view?usp=sharing"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 border-2 border-primary-500/70 text-primary-400 font-semibold rounded-full hover:bg-primary-500/10 hover:border-primary-500 transition-all duration-300 inline-block backdrop-blur-sm"
              whileTap={{ scale: 0.95 }}
            >
              Download CV
            </motion.a>
          </Magnet>
        </motion.div>
      </motion.div>

      {/* Scroll indicator — fades out as soon as scrolling starts */}
      <motion.div
        className="absolute bottom-24 left-1/2 transform -translate-x-1/2"
        style={{ opacity: indicatorOpacity }}
      >
        <motion.div
          className="flex flex-col items-center gap-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.5 }}
        >
          <span className="text-xs text-gray-500 tracking-widest uppercase">Scroll</span>
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity }}>
            <ChevronDown className="w-5 h-5 text-gray-500" />
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
